"use client";

import { getCountryFlagIconComponent } from "@/utils/country/getCountryFlagIconComponent";
import { faGlobe, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, Col, Row } from "antd";
import { FC, memo, useState } from "react";

// Ảnh `/image/{code}_popular.png` chưa tồn tại thật cho hầu hết quốc gia — nhớ lại (module-level,
// sống hết phiên trang, mất khi tải lại) những mã đã thử và lỗi 1 lần, để card bị ẩn/hiện lại (gõ
// tìm kiếm) hoặc mount lại không thử tải lại ảnh đó nữa, tránh request 404 lặp lại vô ích.
const knownMissingImageCodes = new Set<string>();

type EsimDestinationCardProps = {
  /** ISO alpha-2, lowercase — thiếu (card Khu Vực) thì fallback icon `faGlobe`. */
  countryCode?: string;
  name: string;
  description: string;
  /** Mặc định `/image/{countryCode}_popular.png` — repo chưa có ảnh thật, lỗi tải thì fallback cờ quốc gia. */
  imageSrc?: string;
  onClick: () => void;
};

/** Card dùng chung cho 3 tab Phổ biến/Quốc gia/Khu Vực (`EsimHubTabs`) — ảnh ~2/3 chiều cao + hàng
 * icon/tên/mô tả bên dưới. Style chi tiết (bo góc, shadow, hover, zoom ảnh) nằm ở
 * `src/styles/pages/_esim.scss` (`.esim-destination-card`), không inline — cần `:hover` cho hiệu ứng.
 *
 * Bọc `memo` — danh sách cha (vd tab Quốc gia) re-render liên tục khi gõ tìm kiếm (đổi `display` ẩn/
 * hiện từng card), nếu không memo thì TOÀN BỘ card đang mount sẽ chạy lại render mỗi lần gõ dù props
 * không đổi. Chỉ có tác dụng khi `onClick` truyền vào ổn định giữa các lần render (xem cách gọi trong
 * `EsimHubTabs` — cache theo mã, không tạo hàm mới mỗi lần `.map()`). */
const EsimDestinationCardImpl: FC<EsimDestinationCardProps> = ({
  countryCode,
  name,
  description,
  imageSrc,
  onClick,
}) => {
  const alreadyKnownMissing = !!countryCode && knownMissingImageCodes.has(countryCode);
  const [imageFailed, setImageFailed] = useState(alreadyKnownMissing);
  const resolvedImageSrc = alreadyKnownMissing
    ? undefined
    : imageSrc ?? (countryCode ? `/image/${countryCode}_popular.png` : undefined);
  const showImage = !!resolvedImageSrc && !imageFailed;
  // SVG inline (country-flag-icons) — không tốn request mạng như flagcdn, dùng làm fallback lấp đầy
  // toàn bộ vị trí ảnh (thay vì icon tròn nhỏ như CountryFlag) khi chưa có ảnh thật cho quốc gia đó.
  const FlagIcon = !showImage ? getCountryFlagIconComponent(countryCode) : undefined;

  const handleImageError = () => {
    if (countryCode) knownMissingImageCodes.add(countryCode);
    setImageFailed(true);
  };

  return (
    <Card
      hoverable
      onClick={onClick}
      className="esim-destination-card"
      cover={
        <div className="esim-destination-card__cover">
          {showImage ? (
            <img
              alt={name}
              src={resolvedImageSrc}
              onError={handleImageError}
              className="esim-destination-card__image"
            />
          ) : FlagIcon ? (
            <FlagIcon
              title={name}
              preserveAspectRatio="none"
              className="esim-destination-card__image esim-destination-card__image--flag"
            />
          ) : (
            <FontAwesomeIcon icon={faGlobe} className="esim-destination-card__globe-icon" />
          )}
        </div>
      }
    >
      <Row wrap={false} align="middle" gutter={10}>
        <Col flex="none">
          <span className="esim-destination-card__pin">
            <FontAwesomeIcon icon={faMapMarkerAlt} />
          </span>
        </Col>
        <Col flex="auto" style={{ minWidth: 0 }}>
          <div className="esim-destination-card__name">{name}</div>
          <div className="esim-destination-card__desc">{description}</div>
        </Col>
      </Row>
    </Card>
  );
};

const EsimDestinationCard = memo(EsimDestinationCardImpl);

export default EsimDestinationCard;
